import debounce from 'debounce';

/**
 * This function hides the intercom chat widget on mobile when
 * scrolling down to allow for more screen space. It uses observers
 * as after a user clicks the widget it transforms into a different element.
 */
const initIntercomWidgetHandler = () => {
  const mobileBreakpoint = 640;

  let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollThreshold = 10;
  let intercomElements = [];
  let isMobile = window.innerWidth < mobileBreakpoint;
  let observer = null;

  // Event handler functions (defined with const to keep them in scope)
  const handleScroll = debounce(() => {
    if (!isMobile || intercomElements.length === 0) return;

    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Determine scroll direction and apply appropriate transform
    if (Math.abs(currentScrollTop - lastScrollTop) <= scrollThreshold) return;

    if (currentScrollTop > lastScrollTop) {
      // Scrolling down - hide elements by sliding to the right
      applyStyles(intercomElements, {
        transform: 'translateX(150%)'
      });
    } else {
      // Scrolling up - show elements
      applyStyles(intercomElements, {
        transform: 'translateX(0)'
      });
    }

    lastScrollTop = currentScrollTop;
  }, 10);

  const handleResize = debounce(() => {
    const wasMobile = isMobile;
    isMobile = window.innerWidth < mobileBreakpoint;

    // If transitioning between mobile and desktop states, reset styles
    if (wasMobile !== isMobile && intercomElements.length > 0) {
      applyStyles(intercomElements, {
        transform: '',
        transition: ''
      });

      // If now mobile, reapply transition style
      if (isMobile) {
        applyStyles(intercomElements, {
          transition: 'transform 0.3s ease-out'
        });
      }
    }
  }, 100);

  const applyStyles = (elements, styles) => {
    elements.forEach(element => {
      Object.assign(element.style, styles);
    });
  };

  const findIntercomElements = () => {
    const launcherElement = document.querySelector('.intercom-lightweight-app-launcher');
    const namespaceElement = document.querySelector('.intercom-with-namespace-52k34s');

    const newElements = [];

    if (launcherElement && !intercomElements.includes(launcherElement)) {
      newElements.push(launcherElement);
    }

    if (namespaceElement && !intercomElements.includes(namespaceElement)) {
      newElements.push(namespaceElement);
    }

    if (newElements.length > 0) {
      intercomElements = [...intercomElements, ...newElements];
      applyStyles(intercomElements, {
        transition: 'transform 0.3s ease-out'
      });

      // Add the safe area positioning class for iPhone bottom bar
      newElements.forEach(element => {
        element.classList.add('safe-area-bottom-positioning');
      });
    }
  };

  const setupObserver = () => {
    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          findIntercomElements();
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const setupEventListeners = () => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
  };

  const cleanup = () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleResize);
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  // Initialize everything
  const init = () => {
    setupObserver();
    setupEventListeners();
    findIntercomElements();

    return cleanup;
  };

  return init();
};

const setupIntercomHandler = () => {
  let cleanupFunction = null;

  // Initialize on first page load
  document.addEventListener('DOMContentLoaded', () => {
    cleanupFunction = initIntercomWidgetHandler();
  });

  // Handle Turbo navigation events
  document.addEventListener('turbo:load', () => {
    // Clean up previous instance if it exists
    if (cleanupFunction) {
      cleanupFunction();
    }

    cleanupFunction = initIntercomWidgetHandler();
  });

  // Only reinitialize for full-page frame loads
  document.addEventListener('turbo:frame-load', (event) => {
    if (event.target.id === 'turbo-body' || event.target.tagName === 'BODY') {
      if (cleanupFunction) {
        cleanupFunction();
      }
      cleanupFunction = initIntercomWidgetHandler();
    }
  });
};

setupIntercomHandler();
